import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/config/site";
import { Logo } from "./Logo";
import { DonateButton } from "./Primitives";
import { getLenis } from "./SmoothScroll";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const MobileMenu = ({ onClose }) => (
  <motion.div
    id="mobile-menu"
    className="fixed inset-0 z-40 flex flex-col bg-navy px-6 pb-10 pt-28 xl:hidden"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    data-testid="mobile-menu"
  >
    <nav aria-label="Mobile" className="flex flex-col">
      {NAV.map((item, i) => (
        <motion.div key={item.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i + 0.1 }}>
          <NavLink
            to={item.to}
            end
            onClick={onClose}
            className={({ isActive }) => `block border-b border-white/10 py-4 font-display text-2xl font-bold ${isActive ? "text-gold" : "text-paper"}`}
            data-testid={`mobile-nav-${slug(item.label)}`}
          >
            {item.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <DonateButton label="Donate Now" className="mt-10 w-full" testId="mobile-nav-donate-button" />
  </motion.div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const l = getLenis();
    if (open) l?.stop();
    else l?.start();
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const solid = scrolled || open;
  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        solid ? "bg-navy/90 py-3 shadow-[0_10px_40px_rgba(11,27,45,0.25)] backdrop-blur-xl" : "bg-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:bg-gold focus:px-4 focus:py-2 focus:text-navy" data-testid="skip-to-content-link">
        Skip to content
      </a>
      <div className="container-x flex items-center justify-between gap-6">
        <Link to="/" aria-label="First Responders Foundation home" data-testid="header-logo-link">
          <Logo />
        </Link>
        <nav aria-label="Primary" className="hidden items-center gap-5 2xl:gap-8 xl:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) => `link-underline whitespace-nowrap text-[13px] font-medium tracking-wide ${isActive ? "text-gold" : "text-paper/90 hover:text-paper"}`}
              data-testid={`nav-${slug(item.label)}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <DonateButton className="!px-5 !py-3 !min-h-[44px]" testId="nav-donate-button" />
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-paper xl:hidden"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>
    <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
